/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { applyProgressToBlockStreamState } from '../helpers/thoughtProgressCoalesce';
// ---------------------------------------------------------------------------
// Initial block stream state (mirrors useBlockStream INITIAL_STATE)
// ---------------------------------------------------------------------------
const INITIAL_STATE = {
    responseId: null,
    conversationId: null,
    turnNumber: null,
    agent: null,
    blocks: [],
    streamingBlockId: null,
    streamingBlockType: null,
    streamingContent: '',
    isStreaming: false,
    responseState: 'idle',
    suggestedActions: [],
    durationMs: 0,
    feedbackId: null,
    pendingApproval: null,
    progressPhase: null,
    progressThoughtKey: null,
    progressMessage: null,
    progressDetail: null,
    progressAgent: null,
    pendingNudge: null
};
function buildReducers() {
    return {
        stream_start: (payload, prev) => {
            var _a;
            const p = payload;
            return Object.assign(Object.assign({}, prev), { responseId: p.response_id, conversationId: p.conversation_id, turnNumber: (_a = p.turn_number) !== null && _a !== void 0 ? _a : null, agent: p.agent, isStreaming: true, responseState: 'receiving', pendingApproval: null, progressPhase: null, progressThoughtKey: null, progressMessage: null, progressDetail: null, progressAgent: null });
        },
        block_start: (payload, prev) => {
            const p = payload;
            return Object.assign(Object.assign({}, prev), { streamingBlockId: p.block_id, streamingContent: '' });
        },
        block_delta: (payload, prev) => {
            const p = payload;
            if (p.delta.kind === 'text' && p.delta.text) {
                return Object.assign(Object.assign({}, prev), { streamingContent: prev.streamingContent + p.delta.text });
            }
            if (p.delta.kind === 'step' && p.delta.step) {
                const step = p.delta.step;
                const liveIdx = prev.blocks.findIndex((b) => b.block_id === prev.streamingBlockId && b.type === 'tool_progress');
                if (liveIdx < 0)
                    return prev;
                /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
                // Test fixture: loosely typed for mock data manipulation
                const liveBlock = prev.blocks[liveIdx];
                const steps = (liveBlock.steps || []);
                const existIdx = steps.findIndex((s) => s.step_id === step.step_id);
                const updatedSteps = existIdx >= 0 ? steps.map((s, i) => (i === existIdx ? step : s)) : [...steps, step];
                const updatedBlock = Object.assign(Object.assign({}, liveBlock), { steps: updatedSteps });
                const updatedBlocks = prev.blocks.map((b, i) => (i === liveIdx ? updatedBlock : b));
                /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
                return Object.assign(Object.assign({}, prev), { blocks: updatedBlocks });
            }
            return prev;
        },
        block_end: (_payload, prev) => {
            if (!prev.streamingBlockId)
                return prev;
            return Object.assign(Object.assign({}, prev), { blocks: [
                    ...prev.blocks,
                    {
                        type: 'markdown',
                        block_id: prev.streamingBlockId,
                        block_index: prev.blocks.length,
                        content: prev.streamingContent
                    }
                ], streamingBlockId: null, streamingContent: '' });
        },
        block_complete: (payload, prev) => {
            const p = payload;
            return Object.assign(Object.assign({}, prev), { blocks: [...prev.blocks, p.block], streamingBlockId: null, streamingContent: '' });
        },
        approval_required: (payload, prev) => {
            const p = payload;
            return Object.assign(Object.assign({}, prev), { pendingApproval: p, responseState: 'waiting_approval', blocks: (p === null || p === void 0 ? void 0 : p.confirmation) ? [...prev.blocks, p.confirmation] : prev.blocks });
        },
        progress: (payload, prev) => {
            const p = payload;
            return applyProgressToBlockStreamState(prev, p);
        },
        stream_end: (payload, prev) => {
            var _a;
            const p = payload;
            return Object.assign(Object.assign({}, prev), { turnNumber: (_a = p.turn_number) !== null && _a !== void 0 ? _a : prev.turnNumber, isStreaming: false, responseState: 'complete', durationMs: p.duration_ms, feedbackId: p.feedback_id, suggestedActions: p.suggested_actions || [], pendingApproval: null, progressPhase: null, progressThoughtKey: null, progressMessage: null, progressDetail: null, progressAgent: null });
        }
    };
}
const MockSSEContext = React.createContext(null);
export function useMockSSE() {
    const ctx = React.useContext(MockSSEContext);
    if (!ctx)
        throw new Error('useMockSSE must be used within MockSSEProvider');
    return ctx;
}
export function MockSSEProvider({ children }) {
    const queryClientRef = useRef(new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } } }));
    const reducers = useRef(buildReducers());
    const timeoutsRef = useRef(new Map());
    const replaySequence = useCallback((sequence, convId) => {
        const queryKey = ['ai-chat', 'stream', convId];
        let cumulativeDelay = 0;
        const timeoutIds = [];
        const existingTimeouts = timeoutsRef.current.get(convId) || [];
        existingTimeouts.forEach((id) => {
            window.clearTimeout(id);
        });
        sequence.forEach((event) => {
            cumulativeDelay += event.delayMs;
            const timeoutId = window.setTimeout(() => {
                queryClientRef.current.setQueryData(queryKey, (prev) => {
                    const state = prev || INITIAL_STATE;
                    const reducer = reducers.current[event.type];
                    if (!reducer)
                        return state;
                    return reducer(event.payload, state);
                });
            }, cumulativeDelay);
            timeoutIds.push(timeoutId);
        });
        timeoutsRef.current.set(convId, timeoutIds);
    }, []);
    const stopSequence = useCallback((convId) => {
        // Cancel all pending timeouts for this conversation
        const timeoutIds = timeoutsRef.current.get(convId) || [];
        timeoutIds.forEach((id) => window.clearTimeout(id));
        timeoutsRef.current.delete(convId);
        // Update query state to mark streaming as stopped
        const queryKey = ['ai-chat', 'stream', convId];
        queryClientRef.current.setQueryData(queryKey, (prev) => {
            if (!prev || !prev.isStreaming)
                return prev;
            return Object.assign(Object.assign({}, prev), { isStreaming: false, responseState: 'complete', streamingBlockId: null, streamingContent: '' });
        });
    }, []);
    const handle = React.useMemo(() => ({ replaySequence, stopSequence }), [replaySequence, stopSequence]);
    return (React.createElement(MockSSEContext.Provider, { value: handle },
        React.createElement(QueryClientProvider, { client: queryClientRef.current }, children)));
}
